import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils.js';

/**
 * Shared between the Experience page and the Admin Console, which render in
 * different colour schemes, so every colour here is a semantic token from
 * `index.css` rather than a literal. A component that hardcoded `bg-white`
 * would need a dark variant threading through it.
 */

export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  return (
    <button
      {...props}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-button px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-brand text-white hover:opacity-90',
        variant === 'secondary' && 'border border-line bg-surface text-body hover:bg-hover',
        variant === 'danger' &&
          'border border-danger-line bg-surface text-danger hover:bg-danger-surface',
        className,
      )}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        'w-full rounded-brand border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-muted focus:border-brand',
        className,
      )}
    />
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-brand border border-line bg-surface', className)}>{children}</div>
  );
}

export function SectionHeading({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  queued: 'bg-subtle text-body',
  running: 'bg-info-bg text-info',
  pending: 'bg-subtle text-body',
  staged: 'bg-info-bg text-info',
  succeeded: 'bg-success-bg text-success',
  uploaded: 'bg-success-bg text-success',
  partial: 'bg-warning-bg text-warning',
  rejected: 'bg-warning-bg text-warning',
  failed: 'bg-danger-bg text-danger',
  active: 'bg-success-bg text-success',
  needs_reauth: 'bg-warning-bg text-warning',
  revoked: 'bg-danger-bg text-danger',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        STATUS_STYLES[status] ?? 'bg-subtle text-body',
      )}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-brand border border-dashed border-line p-8 text-center">
      <p className="text-sm font-medium text-body">{title}</p>
      {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <p className="animate-pulse py-8 text-center text-sm text-muted">{label}</p>;
}

export function ErrorNotice({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="rounded-brand border border-danger-line bg-danger-surface p-4 text-sm text-danger">
      {message}
    </div>
  );
}
